// @flow
import React from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import moment from 'moment'
import SearchList from 'components/pages/SearchPage/List'
import type { Room, Thumbnail } from 'types/www/room'
import type { PageInfo } from 'types/www/pageInfo'
import {
  imageURLWithTransform,
  roomName,
} from 'frontend_core/dist/helpers/format'
import { isMobile } from 'frontend_core/dist/helpers/screen'
import SearchHelper, {
  convertToSearchConditionType,
  queryStringStringify,
} from 'helpers/search'
import Path from 'routes/path'
import type { Amenity } from 'types/www/master'
import { containerIds, setUIState } from 'redux/modules/ui'
import {
  type SearchConditionType,
  setSearchCondition,
} from 'redux/modules/search'

type PropTypes = {
  amenities: Array<Amenity>,
  sponsoredPromotions: Array<Object>,
  location: {
    pathname: string,
    search: string,
  },
  perPage: number,
  searchSessionId: string,
  // 現在の検索結果に対する検索条件
  ui: {
    // エリア選択のオートコンプリート保持用
    autoCompleteItems: Array<Object>,
    isOpenedAutocomplete: boolean,
    selectedPlaceId: ?string,
    // ドロップダウン＆もっと見るの状態管理
    showCheckInCalendar: boolean,
    showCapacity: boolean,
    showAmenityMore: boolean,
    showSearchForm: boolean,
    // 日付選択のドロップダウン時の一時保持用
    startedAt: ?moment,
    endedAt: ?moment,
    focusedInput: ?string,
    // キャパシティのドロップダウン時の一時保持用
    minCapacity: ?number,
    // スライダーの表示
    isBudgetUnspecified: boolean,
    // 表示用検索条件（実際の検索結果に該当するのははprops.searchCondition側）
    searchCondition: SearchConditionType,
    isOpenFavoriteSelectModal: boolean,
    addedFavoriteRoomIds: Array<number>,
    deletedFavoriteRoomIds: Array<number>,
  },
  history: {
    push: Function,
    replace: Function,
  },
  isLoading: boolean,
  onClickFavorite: Function,
  viewer: Object,
  roomDetail: Object,
}

class Container extends React.Component<PropTypes> {
  constructor(props: PropTypes) {
    super(props)
    this.searchFormOnSubmit = this.searchFormOnSubmit.bind(this)
    this.canDisplayedFav = this.canDisplayedFav.bind(this)
  }

  componentDidMount() {
    const searchCondition = this.props.search

    // 初めてきた時はクエリからUIステートをセットする
    let uiState = {}
    uiState.focusedInput = SearchHelper.focusedInputStartDate
    uiState.minPrice = searchCondition.minPrice
    uiState.maxPrice = searchCondition.maxPrice
    uiState.isBudgetUnspecified = !uiState.minPrice && !uiState.maxPrice
    uiState.searchCondition = Object.assign({}, searchCondition)
    if (searchCondition.startedAt !== '' && searchCondition.endedAt !== '') {
      uiState = Object.assign(uiState, {
        startedAt: moment(searchCondition.startedAt),
        endedAt: moment(searchCondition.endedAt),
      })
    }
    if (searchCondition.minCapacity) {
      uiState = Object.assign(uiState, {
        minCapacity: searchCondition.minCapacity,
      })
    }
    this.props.actions.setUIState(containerIds.SEARCH, uiState)
  }

  searchFormOnSubmit(e) {
    e.preventDefault()
    window.scrollTo(0, 0)
    // モバイルなら検索ボックスを非表示にする
    if (isMobile()) {
      this.props.actions.setUIState(containerIds.SEARCH, {
        showSearchForm: false,
        isShowHeaderSearch: false,
      })
    }
  }

  canDisplayedFav(room: Object) {
    const { roomDetail, ui } = this.props

    if (roomDetail.roomId === room.id) return roomDetail.isFavorite
    if (ui.addedFavoriteRoomIds.indexOf(room.id) >= 0) return true
    if (ui.deletedFavoriteRoomIds.indexOf(room.id) >= 0) return false

    return room.isFavorite
  }

  render() {
    const { ui, rooms, pageInfo, viewer } = this.props
    const searchCondition = this.props.search

    let roomCardsProps = []
    if (Array.isArray(rooms)) {
      roomCardsProps = rooms.map(room => {
        const price =
          Array.isArray(room.prices) && room.prices.length > 0
            ? room.prices[0]
            : {}
        const unitText = price.minUnitText || price.maxUnitText || ''
        const priceText = `${price.minText || ''}${
          price.maxText ? `〜${price.maxText}` : ''
        }/${unitText}`

        const thumbnails = room.thumbnails.results
        const items = thumbnails.map((thumbnail: Thumbnail) => ({
          imageUrl: imageURLWithTransform(thumbnail.url || '', {
            width: 840,
            height: 560,
            crop: 'fill',
          }),
          imageAlt: thumbnail.description,
        }))

        const space = room.space || {}
        const owner = room.owner || space.owner || {}
        const url =
          Path.room(space.username, room.uid || '') +
          `?searchSessionId=${this.props.searchSessionId}`
        const reputationSummary = room.reputationSummary || {}

        return {
          id: room.id,
          uid: room.uid,
          isFavorite: room.isFavorite,
          roomLink: url,
          thumbnails: items,
          title: roomName(room),
          price: priceText,
          isDirectReservation: room.directReservationAccepted,
          isTopHost: owner.rank === 1,
          isLastMinutesDiscount: room.isLastMinuteDiscount,
          sponsoredPromotions: room.sponsoredPromotions.results,
          capacity: i18n.t('page.room.capacity', {
            capacity: room.capacity || '',
          }),
          location: space.stateText || '',
          reviewScore: reputationSummary.score || 0,
          reviewCount: reputationSummary.count || 0,
          reviewLink: '',
          access: space.access,
        }
      })
    }

    return (
      <SearchList
        amenities={this.props.amenities}
        sponsoredPromotions={this.props.sponsoredPromotions}
        eventTypes={this.props.eventTypes}
        capacities={this.props.capacities}
        pageInfo={this.props.pageInfo}
        searchFormOnSubmit={this.searchFormOnSubmit}
        location={this.props.location}
        perPage={this.props.perPage}
        searchSessionId={this.props.searchSessionId}
        roomCardPropsList={roomCardsProps}
        totalCount={`${pageInfo.totalCount}`}
        showSearchForm={ui.showSearchForm}
        onClickViewType={() => {
          this.props.actions.setSearchCondition(
            convertToSearchConditionType(this.props.location.search),
          )
          this.props.actions.setUIState(containerIds.SEARCH, {
            isShowMap: true,
            searchAtListView: this.props.location.search,
          })
        }}
        mapViewLink={`${Path.searchMap()}?${queryStringStringify(
          Object.assign(searchCondition, {
            searchSessionId: this.props.searchSessionId,
          }),
        )}`}
        isLoading={this.props.isLoading}
        onClickFavorite={this.props.onClickFavorite}
        viewer={viewer}
      />
    )
  }
}

function mapStateToProps(state: Object) {
  return state
}
function mapDispatchToProps(dispatch: Dispatch<*>) {
  const bindActions = { setUIState, setSearchCondition }
  return { actions: bindActionCreators(bindActions, dispatch) }
}
export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(Container),
)



// WEBPACK FOOTER //
// ./src/containers/pages/SearchPage/List.jsx